// The single permission map for web, app and API.
//
// Two layers:
//  1. Entra *app roles* (in the token's `roles` claim) decide who can sign in
//     and to which shell, and who is a platform admin. Assign them to the NU-
//     groups in Entra, not to individual users. Nothing calls Graph per
//     request, which avoids the 20-group checkMemberGroups cap NFUK hit.
//  2. Page access, kept in our own database (Platform > Permissions): for each
//     page, a person has no access, can view or can edit. Each page has a
//     default by role; an admin can set anyone's level for a page on top.
//
// Screens, nav items and API routes check capabilities, never role names.

export const ROLES = Object.freeze({
  ADMIN: 'Platform.Admin',
  OFFICE: 'Office.User',
  FIELD: 'Field.Engineer',
  SUPERVISOR: 'Field.Supervisor',
  STORES: 'Stores.User',
  // No longer grants anything (the helpdesk queue is a page permission now);
  // kept so the Entra role still makes sense until it's removed.
  HELPDESK: 'Helpdesk.Agent',
});

// Who can sign in and where: capability -> roles that grant it.
export const CAPABILITIES = Object.freeze({
  'app.use': [ROLES.ADMIN, ROLES.OFFICE, ROLES.FIELD, ROLES.SUPERVISOR, ROLES.STORES],
  'office.use': [ROLES.ADMIN, ROLES.OFFICE, ROLES.SUPERVISOR, ROLES.STORES],
  'field.use': [ROLES.ADMIN, ROLES.FIELD, ROLES.SUPERVISOR],
  'platform.admin': [ROLES.ADMIN],
});

// A page's default (30/10/2026): only master admins have it. Everyone else
// starts with no access and is given pages one by one on Platform >
// Permissions, so someone newly added to an NU group sees nothing until then.
const ADMINS_ONLY = [ROLES.ADMIN];

// Where each page sits on Platform > Permissions: the sidebar departments,
// plus Helpdesk (a top-level item).
export const PAGE_GROUPS = Object.freeze([
  { id: 'operations', label: 'Operations' },
  { id: 'communications', label: 'Communications' },
  { id: 'fleet', label: 'Fleet' },
  { id: 'stock', label: 'Stock' },
  { id: 'helpdesk', label: 'Helpdesk' },
  { id: 'platform', label: 'Platform' },
]);

// The pages whose access is managed per person. `levels` is what a person can
// be given: ['view', 'edit'], or ['edit'] alone for a page that's all-or-
// nothing (shown as "Has access"). `defaults` is the level each role gets
// unless someone has been set otherwise. `grants` are the API capabilities
// each level brings: several pages share an API (the bookings ones), so any
// of them opens it, and each screen checks its own page before editing.
// `app: true`: the page is also a screen in the phone app, so people with
// the field app only can be given it too (Network Plus surveys: Surveys).
export const PAGES = Object.freeze([
  { id: 'bookings-calendar', group: 'operations', label: 'Bookings calendar', levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['bookings.view'], edit: ['bookings.edit'] } },
  { id: 'bookings-list', group: 'operations', label: 'Bookings list', levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['bookings.view'], edit: ['bookings.edit'] } },
  { id: 'plan', group: 'operations', label: 'Plan the day', levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['bookings.view'], edit: ['bookings.edit'] } },
  { id: 'whereabouts', group: 'operations', label: 'Whereabouts', levels: ['view'], defaults: { view: ADMINS_ONLY }, grants: { view: ['whereabouts.view'] } },
  { id: 'pulse', group: 'operations', label: 'Pulse', levels: ['view'], defaults: { view: ADMINS_ONLY }, grants: { view: ['pulse.view'] } },
  { id: 'job-times', group: 'operations', label: 'Job Card Time Analyser', levels: ['view'], defaults: { view: ADMINS_ONLY }, grants: { view: ['jobtimes.view'] } },
  { id: 'defects', group: 'operations', label: 'Defects', levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['bookings.view'], edit: ['bookings.edit'] } },
  { id: 'incidents', group: 'operations', label: 'Incidents', levels: ['view'], defaults: { view: ADMINS_ONLY }, grants: { view: ['bookings.view'] } },
  { id: 'surveys', group: 'operations', label: 'Network Plus surveys', app: true, levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['surveys.view'], edit: ['surveys.edit'] } },
  { id: 'matchups', group: 'operations', label: 'Match-Ups', levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['matchups.view'], edit: ['matchups.edit'] } },
  { id: 'comms', group: 'communications', label: 'Send a message', levels: ['edit'], defaults: { edit: ADMINS_ONLY }, grants: { edit: ['comms.send'] } },
  { id: 'comms-activity', group: 'communications', label: 'Recipient activity', levels: ['view'], defaults: { view: ADMINS_ONLY }, grants: { view: ['comms.send'] } },
  { id: 'vans', group: 'fleet', label: 'Vans', levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['fleet.view'], edit: ['fleet.edit'] } },
  { id: 'stock-counts', group: 'stock', label: 'UT stock counts', levels: ['view'], defaults: { view: ADMINS_ONLY }, grants: { view: ['stock.view'] } },
  { id: 'signatures', group: 'platform', label: 'Email signatures', levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['signatures.view'], edit: ['signatures.view', 'signatures.edit'] } },
  { id: 'stock-job-layout', group: 'stock', label: 'Job card stock organizer', levels: ['view', 'edit'], defaults: { edit: ADMINS_ONLY }, grants: { view: ['stock.layout'], edit: ['stock.layout.edit'] } },
  { id: 'stock-job-used', group: 'stock', label: 'Stock used on jobs', levels: ['view'], defaults: { view: ADMINS_ONLY }, grants: { view: ['stock.jobused'] } },
  { id: 'stock-orders', group: 'stock', label: 'Orders', levels: ['view'], defaults: { view: ADMINS_ONLY }, grants: { view: ['stock.orders'] } },
  { id: 'helpdesk-queue', group: 'helpdesk', label: 'Helpdesk queue', levels: ['edit'], defaults: {}, grants: { edit: ['helpdesk.work'] } },
]);

export const PAGE_BY_ID = Object.freeze(Object.fromEntries(PAGES.map((p) => [p.id, p])));
export const PAGE_LEVELS = Object.freeze(['none', 'view', 'edit']);

// Every capability there is: sign-in ones, each page's own, and what the
// pages grant.
const PAGE_CAPS = PAGES.flatMap((p) => [`page.${p.id}.view`, `page.${p.id}.edit`, ...Object.values(p.grants).flat()]);
export const ALL_CAPABILITIES = Object.freeze([...new Set([...Object.keys(CAPABILITIES), ...PAGE_CAPS])]);

const pageOf = (page) => (typeof page === 'string' ? PAGE_BY_ID[page] : page);

// The level a set of roles gets on a page before anyone changes it.
export function defaultPageLevel(page, roles = []) {
  const p = pageOf(page);
  if (!p || !Array.isArray(roles)) return 'none';
  if (p.defaults.edit?.some((r) => roles.includes(r))) return 'edit';
  if (p.defaults.view?.some((r) => roles.includes(r))) return p.levels.includes('view') ? 'view' : 'edit';
  return 'none';
}

// `overrides` is { pageId: 'none' | 'view' | 'edit' } from the database. A
// 'view' on an all-or-nothing page is read as access.
export function pageLevel(page, roles, overrides = {}) {
  const p = pageOf(page);
  const set = overrides?.[p.id];
  if (PAGE_LEVELS.includes(set)) return set === 'view' && !p.levels.includes('view') ? 'edit' : set;
  return defaultPageLevel(p, roles);
}

function signInCan(roles, capability) {
  return Array.isArray(roles) && roles.some((r) => CAPABILITIES[capability].includes(r));
}

// Everything a person may do: their roles' sign-in capabilities, then each
// page at its level (default by role, or as set on Platform > Permissions).
export function capabilitiesFor(roles, overrides = {}) {
  const out = new Set(Object.keys(CAPABILITIES).filter((c) => signInCan(roles, c)));
  if (!out.has('app.use')) return [...out];
  for (const p of PAGES) {
    const level = pageLevel(p, roles, overrides);
    if (level === 'none') continue;
    out.add(`page.${p.id}.view`);
    (p.grants.view || []).forEach((c) => out.add(c));
    if (level === 'edit') {
      out.add(`page.${p.id}.edit`);
      (p.grants.edit || []).forEach((c) => out.add(c));
    }
  }
  return [...out];
}

// From roles alone (page defaults, no overrides).
export function can(roles, capability) {
  if (!ALL_CAPABILITIES.includes(capability)) throw new Error(`Unknown capability: ${capability}`);
  return CAPABILITIES[capability] ? signInCan(roles, capability) : capabilitiesFor(roles).includes(capability);
}

// For code holding a signed-in user (req.user, a sync handler's user): uses
// the capabilities worked out at sign-in, overrides included.
export function has(user, capability) {
  if (!ALL_CAPABILITIES.includes(capability)) throw new Error(`Unknown capability: ${capability}`);
  const caps = Array.isArray(user?.capabilities) ? user.capabilities : capabilitiesFor(user?.roles);
  return caps.includes(capability);
}
